<template>
  <main class="tool-page tool-page-wide">
    <div class="timestamp-grid">
      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Timestamp</p>
            <h2>时间转换</h2>
          </div>
          <el-button plain @click="fillNow">填入当前时间</el-button>
        </div>

        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="时间字符串">
            <el-date-picker
              v-model="dateValue"
              type="datetime"
              placeholder="选择日期时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              format="yyyy-MM-dd HH:mm:ss"
              class="full-width"
            />
          </el-form-item>

          <div class="result-pair">
            <button class="result-chip" type="button" @click="copyText(secondsValue, '已复制秒级时间戳')">
              <span class="result-chip-label">秒级时间戳</span>
              <strong>{{ secondsValue || "--" }}</strong>
            </button>
            <button
              class="result-chip"
              type="button"
              @click="copyText(millisecondsValue, '已复制毫秒级时间戳')"
            >
              <span class="result-chip-label">毫秒级时间戳</span>
              <strong>{{ millisecondsValue || "--" }}</strong>
            </button>
          </div>
        </el-form>
      </el-card>

      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Seconds</p>
            <h2>秒时间戳</h2>
          </div>
        </div>

        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="秒级时间戳">
            <div class="field-row">
              <el-input v-model.trim="secondsValue" placeholder="例如 1713168000" />
              <el-button plain @click="copyText(secondsValue, '已复制秒级时间戳')">复制</el-button>
            </div>
          </el-form-item>

          <p class="status" :class="secondsStatus.type">{{ secondsStatus.message }}</p>
        </el-form>
      </el-card>

      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Milliseconds</p>
            <h2>毫秒时间戳</h2>
          </div>
        </div>

        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="毫秒级时间戳">
            <div class="field-row">
              <el-input v-model.trim="millisecondsValue" placeholder="例如 1713168000000" />
              <el-button plain @click="copyText(millisecondsValue, '已复制毫秒级时间戳')">复制</el-button>
            </div>
          </el-form-item>

          <p class="status" :class="millisecondsStatus.type">{{ millisecondsStatus.message }}</p>
        </el-form>
      </el-card>
    </div>
  </main>
</template>

<script>
const formatInTimeZone = (date) =>
  new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

const parseTimestamp = (value, expectedLength) => {
  const cleaned = value.trim();
  if (!/^-?\d+$/.test(cleaned)) {
    throw new Error("请输入纯数字时间戳");
  }
  if (expectedLength && cleaned.length !== expectedLength) {
    throw new Error(`请输入 ${expectedLength} 位时间戳`);
  }
  return Number(cleaned);
};

const formatDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

export default {
  name: "TimestampPage",
  data() {
    return {
      dateValue: "",
      secondsValue: "",
      millisecondsValue: "",
      timestampStatus: {
        message: "等待输入",
        type: "",
      },
      secondsStatus: {
        message: "等待输入",
        type: "",
      },
      millisecondsStatus: {
        message: "等待输入",
        type: "",
      },
      syncingFrom: "",
    };
  },
  created() {
    this.applyDate(new Date());
  },
  watch: {
    dateValue(value) {
      if (this.syncingFrom) {
        return;
      }
      this.autoConvertDate(value);
    },
    secondsValue(value) {
      if (this.syncingFrom) {
        return;
      }
      this.autoConvertTimestamp(value, 10, "seconds");
    },
    millisecondsValue(value) {
      if (this.syncingFrom) {
        return;
      }
      this.autoConvertTimestamp(value, 13, "milliseconds");
    },
  },
  methods: {
    setStatus(message, type) {
      this.timestampStatus = {
        message,
        type: type || "",
      };
    },
    setFieldStatus(field, message, type) {
      this[field] = {
        message,
        type: type || "",
      };
    },
    autoConvertDate(value) {
      const cleaned = (value || "").trim();
      if (!cleaned) {
        this.secondsValue = "";
        this.millisecondsValue = "";
        this.setFieldStatus("secondsStatus", "等待输入", "");
        this.setFieldStatus("millisecondsStatus", "等待输入", "");
        this.setStatus("等待输入", "");
        return;
      }
      const date = new Date(cleaned.replace(/-/g, "/"));
      if (Number.isNaN(date.getTime())) {
        this.setStatus("时间格式无效", "error");
        return;
      }
      this.applyDate(date);
      this.setStatus(`已转换为 ${formatInTimeZone(date)}`, "success");
    },
    applyDate(date) {
      this.syncingFrom = "date";
      this.dateValue = formatDateTime(date);
      this.secondsValue = String(Math.floor(date.getTime() / 1000));
      this.millisecondsValue = String(date.getTime());
      const message = `对应时间：${formatInTimeZone(date)}`;
      this.setFieldStatus("secondsStatus", message, "success");
      this.setFieldStatus("millisecondsStatus", message, "success");
      this.$nextTick(() => {
        this.syncingFrom = "";
      });
    },
    autoConvertTimestamp(value, expectedLength, source) {
      const cleaned = (value || "").trim();
      const currentField = source === "seconds" ? "secondsStatus" : "millisecondsStatus";
      const otherField = source === "seconds" ? "millisecondsStatus" : "secondsStatus";
      if (!cleaned) {
        this.setFieldStatus(currentField, "等待输入", "");
        return;
      }
      if (!/^-?\d+$/.test(cleaned)) {
        this.setFieldStatus(currentField, "时间戳格式错误", "error");
        return;
      }
      if (cleaned.length !== expectedLength) {
        this.setFieldStatus(
          currentField,
          source === "seconds" ? "请输入 10 位秒级时间戳" : "请输入 13 位毫秒级时间戳",
          "error"
        );
        return;
      }

      try {
        const timestamp = parseTimestamp(cleaned, expectedLength);
        const date = new Date(source === "seconds" ? timestamp * 1000 : timestamp);
        if (Number.isNaN(date.getTime())) {
          throw new Error(source === "seconds" ? "秒级时间戳无效" : "毫秒级时间戳无效");
        }
        this.syncingFrom = source;
        this.dateValue = formatDateTime(date);
        if (source === "seconds") {
          this.millisecondsValue = String(date.getTime());
        } else {
          this.secondsValue = String(Math.floor(date.getTime() / 1000));
        }
        const message = `对应时间：${formatInTimeZone(date)}`;
        this.setFieldStatus(currentField, message, "success");
        this.setFieldStatus(otherField, message, "success");
        this.$nextTick(() => {
          this.syncingFrom = "";
        });
        this.setStatus(
          source === "seconds"
            ? `秒级时间戳已自动转换为 ${formatInTimeZone(date)}`
            : `毫秒级时间戳已自动转换为 ${formatInTimeZone(date)}`,
          "success"
        );
      } catch (error) {
        this.syncingFrom = "";
        this.setFieldStatus(currentField, "时间戳格式错误", "error");
        this.setStatus(error.message, "error");
      }
    },
    fillNow() {
      const now = new Date();
      this.applyDate(now);
      this.setStatus("已填入当前时间", "success");
    },
    async copyText(text, successMessage) {
      const value = (text || "").trim();
      if (!value) {
        this.$message.error("没有可复制的内容");
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        this.$message.success(successMessage);
      } catch (error) {
        this.$message.error("复制失败，请检查浏览器权限");
      }
    },
  },
};
</script>

<style scoped>
.tool-page-wide {
  max-width: 1200px;
}

.timestamp-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.timestamp-grid .panel {
  height: 100%;
}

.result-pair {
  display: grid;
  gap: 12px;
}

.result-chip {
  width: 100%;
  border: 1px solid rgba(74, 56, 33, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76);
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.result-chip:hover {
  transform: translateY(-1px);
  border-color: rgba(200, 92, 53, 0.36);
  box-shadow: 0 12px 28px rgba(93, 59, 22, 0.08);
}

.result-chip-label {
  display: block;
  margin-bottom: 6px;
  color: #7b6855;
  font-size: 0.9rem;
}

.result-chip strong {
  color: #2b2117;
  font-size: 1rem;
  font-family: "SFMono-Regular", "Consolas", "Liberation Mono", monospace;
}

@media (max-width: 960px) {
  .timestamp-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 961px) and (max-width: 1320px) {
  .timestamp-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .timestamp-grid .panel:first-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .timestamp-grid {
    grid-template-columns: 1fr;
  }
}
</style>
